/**
 * Pricing page with transparent pricing tiers.
 * Public-facing page for unauthenticated users.
 */

import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { MarketingLayout } from './MarketingLayout';
import './PricingPage.css';

interface PricingTier {
    name: string;
    platformFee: string;
    perUserFee: string;
    description: string;
    features: string[];
    highlighted: boolean;
}

const pricingTiers: PricingTier[] = [
    {
        name: 'Starter',
        platformFee: '$XXX',
        perUserFee: '$XX',
        description: 'For small IR teams and boutique law firms',
        features: [
            'Up to 10 users',
            'Single workspace',
            'Email support',
        ],
        highlighted: false,
    },
    {
        name: 'Professional',
        platformFee: '$X,XXX',
        perUserFee: '$XX',
        description: 'For mid-size firms with multiple concurrent matters',
        features: [
            'Up to 50 users',
            '5 workspaces',
            'Priority support',
            'Custom branding',
        ],
        highlighted: true,
    },
    {
        name: 'Enterprise',
        platformFee: '$X,XXX',
        perUserFee: '$XX',
        description: 'For large organizations requiring dedicated support',
        features: [
            'Unlimited users',
            'Unlimited workspaces',
            'Dedicated account manager',
            'SLA guarantees',
            'Custom integrations',
        ],
        highlighted: false,
    },
];

const includedFeatures = [
    'Full M365 suite (Teams, SharePoint, Outlook)',
    'Pre-hardened security policies',
    'TAP code generation',
    'B2B guest access',
    'Audit logging',
];

export function PricingPage(): JSX.Element {
    const { isAuthenticated, isLoading } = useAuth();
    const navigate = useNavigate();

    // Redirect authenticated users to the app
    useEffect(() => {
        if (!isLoading && isAuthenticated) {
            navigate('/app/workspaces', { replace: true });
        }
    }, [isAuthenticated, isLoading, navigate]);

    return (
        <MarketingLayout>
            {/* Hero Section */}
            <section className="pricing-hero-section">
                <div className="pricing-hero-container">
                    <h1 className="pricing-hero-headline">
                        Simple, Transparent Pricing
                    </h1>
                    <p className="pricing-hero-subheadline">
                        No hidden fees. No surprise invoices. No &ldquo;contact sales&rdquo; runaround.
                    </p>
                </div>
            </section>

            {/* Pricing Tiers */}
            <section className="pricing-tiers-section">
                <div className="pricing-tiers-container">
                    <div className="pricing-tiers-grid">
                        {pricingTiers.map((tier) => (
                            <div
                                key={tier.name}
                                className={`pricing-tier-card ${tier.highlighted ? 'highlighted' : ''}`}
                            >
                                {tier.highlighted && (
                                    <div className="tier-badge">Most Popular</div>
                                )}
                                <h3 className="tier-name">{tier.name}</h3>
                                <div className="tier-pricing">
                                    <span className="tier-price">{tier.platformFee}</span>
                                    <span className="tier-period">/mo</span>
                                </div>
                                <div className="tier-per-user">
                                    + {tier.perUserFee}/user/mo
                                </div>
                                <p className="tier-description">{tier.description}</p>
                                <ul className="tier-features">
                                    {tier.features.map((feature) => (
                                        <li key={feature}>{feature}</li>
                                    ))}
                                </ul>
                                <Link to="/login" className="tier-cta">
                                    Get Started
                                </Link>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* All Plans Include */}
            <section className="included-section">
                <div className="included-container">
                    <h2 className="included-title">All Plans Include</h2>
                    <div className="included-grid">
                        {includedFeatures.map((feature) => (
                            <div key={feature} className="included-item">
                                <span className="included-check">✓</span>
                                <span className="included-text">{feature}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FAQ Section */}
            <section className="faq-section">
                <div className="faq-container">
                    <h2 className="faq-title">Frequently Asked Questions</h2>
                    <div className="faq-grid">
                        <div className="faq-item">
                            <h4 className="faq-question">How does billing work?</h4>
                            <p className="faq-answer">
                                Platform fees are billed monthly. User fees are calculated based on
                                active users at the end of each billing cycle.
                            </p>
                        </div>
                        <div className="faq-item">
                            <h4 className="faq-question">Can I try before I buy?</h4>
                            <p className="faq-answer">
                                Yes! Sign in with your Microsoft account to explore the platform.
                                You only pay when you provision your first workspace.
                            </p>
                        </div>
                        <div className="faq-item">
                            <h4 className="faq-question">What&apos;s included in the platform fee?</h4>
                            <p className="faq-answer">
                                The platform fee covers workspace provisioning, security policy
                                configuration, and access to all management features.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Bottom CTA */}
            <section className="pricing-cta-section">
                <div className="pricing-cta-container">
                    <h2 className="pricing-cta-title">Ready to get started?</h2>
                    <p className="pricing-cta-description">
                        Enterprise incident response without the enterprise price tag.
                    </p>
                    <Link to="/login" className="pricing-cta-button">
                        Get Started
                    </Link>
                </div>
            </section>
        </MarketingLayout>
    );
}
